import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BookingRequestRead } from '../../../../../js/api/models/BookingRequestRead';
import { BookingRequestReadStatusEnum } from '../../../../../js/api/models/BookingRequestReadStatusEnum';
import type { ConnectedFacility } from '../../../../../js/api/models/ConnectedFacility';
import { ConnectionRequestStatus } from '../../../../../js/api/models/ConnectionRequestStatus';
import { PaginatedBookingRequestReadList } from '../../../../../js/api/models/PaginatedBookingRequestReadList';
import type { PaginatedConnectedFacilityList } from '../../../../../js/api/models/PaginatedConnectedFacilityList';
import { BookingRequestsService } from '../../../../../js/api/services/BookingRequestsService';
import { ConnectedFacilityService } from '../../../../../js/api/services/ConnectedFacilityService';
import { ProfileProgress } from '../../../../../js/app/components/ProfileProgress';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useProvider } from '../../../../../js/app/hooks/providers';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { useUserProgress } from '../../../../../js/app/hooks/user-progress';
import { ProviderSchedule } from '../../../../../js/pages/ProviderDashboard/ProviderSchedule';
import { AllSetStatus } from './AllSetStatus';
import { AssignmentsPendingFacilityResponse } from './AssignmentsPendingFacilityResponse';
import { AssignmentsPendingProviderResponse } from './AssignmentsPendingProviderResponse';
import { ConnectionsPendingFacilityResponse } from './ConnectionsPendingFacilityResponse';
import { ConnectionsPendingProviderResponse } from './ConnectionsPendingProviderResponse';
import { HomeSection } from './HomeSection';
import { ProfileCompletionTasks } from './ProfileCompletionTasks';

export function ProviderHomeWrapper() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { provider } = useProvider();
  const providerId = user?.provider_id;
  const { progress, isProfileComplete } = useUserProgress(
    user?.provider_completed_registration_steps
  );

  useDocumentTitle('Provider Home');

  const connectionsQueryParams: Parameters<
    typeof ConnectedFacilityService.connectedFacilityList
  >[0] = {
    status: [ConnectionRequestStatus.PENDING_RESPONSE],
  };

  const assignmentsQueryParams: Parameters<typeof BookingRequestsService.bookingRequestsList>[0] = {
    // TODO: is there a managed enum for this somewhere?
    statusIn: ['PENDING'],
    date: 'UPCOMING',
  };

  const {
    data: connectionRequests = [],
    isLoading: isConnectionRequestsLoading,
    error: connectionRequestsError,
  } = useQuery({
    queryKey: ['provider-connected-facilities', connectionsQueryParams],
    queryFn: () => ConnectedFacilityService.connectedFacilityList(connectionsQueryParams),
    select: (data: PaginatedConnectedFacilityList) => data.results as ConnectedFacility[],
    staleTime: 30_000,
  });

  const {
    data: assignments = [],
    isLoading: isAssignmentsLoading,
    error: assignmentsError,
  } = useQuery({
    queryKey: ['provider-booking-requests-initial', assignmentsQueryParams],
    queryFn: () => BookingRequestsService.bookingRequestsList(assignmentsQueryParams),
    select: (data: PaginatedBookingRequestReadList) => data.results as BookingRequestRead[],
    staleTime: 30_000,
  });

  const pendingAssignmentRequests = useMemo(
    () => assignments.filter((a) => a.status === BookingRequestReadStatusEnum.PENDING),
    [assignments]
  );

  const hasPendingProfileTasks = useMemo(() => {
    if (!provider) return false;

    const hasCv = Boolean(provider.cv);
    const references = Array.isArray(provider.references) ? provider.references : [];
    const professionalTraitsComplete = hasCv && references.length > 0;

    const licenses = Array.isArray(provider.licenses) ? provider.licenses : [];

    const isMedicalLicenseWithFile = (obj: unknown): boolean => {
      if (typeof obj !== 'object' || obj === null) return false;
      const o = obj as { [key: string]: unknown };
      const isMedical = typeof o['type'] === 'string' && o['type'] === 'MEDICAL';
      const hasFile = 'file' in o && Boolean(o['file']);
      return isMedical && hasFile;
    };

    const licensesComplete = licenses.some(isMedicalLicenseWithFile);
    const questionnaireComplete = Boolean(provider.completed_questionnaire);
    const serviceAgreementComplete = provider.needs_contract_signed === false;

    return (
      !professionalTraitsComplete ||
      !licensesComplete ||
      !questionnaireComplete ||
      !serviceAgreementComplete
    );
  }, [provider]);

  const connectionsPendingFacilityResponse = useMemo(
    () =>
      connectionRequests.filter(
        (c) => c.status === 'accepted' && c.facility_status === 'pending_approval'
      ),
    [connectionRequests]
  );

  const connectionsPendingProviderResponse = useMemo(
    () =>
      connectionRequests.filter(
        (c) => c.status === 'pending_response' && c.facility_status === 'pending_approval'
      ),
    [connectionRequests]
  );

  // Combined loading state - show loading spinner until all API requests complete
  const isLoading = isConnectionRequestsLoading || isAssignmentsLoading;

  // Show loading spinner while any API request is loading
  if (isLoading) {
    return (
      <Container
        className="content-wrapper justify-content-center align-items-center text-center"
        fluid
      >
        <Spinner
          animation="grow"
          aria-label="Loading"
          className="loading-spinner"
          variant="primary"
        />
      </Container>
    );
  }

  return (
    // TODO: decide on background color
    <div className="tw:p-4 tw:pt-0 tw:bg-zinc-100 tw:md:bg-transparent tw:max-w-screen-lg tw:mx-auto tw:w-full">
      {/* Requires Attention Section */}
      {!isConnectionRequestsLoading &&
        !connectionRequestsError &&
        !assignmentsError &&
        (connectionsPendingProviderResponse.length > 0 ||
          pendingAssignmentRequests.length > 0 ||
          hasPendingProfileTasks ||
          !isProfileComplete) && (
          <HomeSection
            headingText="Requires Attention"
            components={[
              // Show ProfileProgress if onboarding is not complete
              !isProfileComplete && (
                <div
                  key="profileProgress"
                  className="tw:bg-white tw:rounded-xl tw:p-4 tw:border tw:border-gray-200"
                >
                  <ProfileProgress progress={progress} />
                </div>
              ),
              // Show other components only if onboarding is complete
              isProfileComplete && (
                <ConnectionsPendingProviderResponse
                  key="connectionsPendingProviderResponse"
                  connectionsPendingProviderResponse={connectionsPendingProviderResponse}
                />
              ),
              isProfileComplete && (
                <AssignmentsPendingProviderResponse
                  key="assignmentsPendingProviderResponse"
                  assignmentsPendingProviderResponse={pendingAssignmentRequests}
                />
              ),
              isProfileComplete && <ProfileCompletionTasks key="profileCompletionTasks" />,
            ].filter(Boolean)}
            actionText=""
            actionFunction={undefined}
          />
        )}

      {/* All Set Status - shown when no actions require attention */}
      {!isConnectionRequestsLoading &&
        !connectionRequestsError &&
        !assignmentsError &&
        connectionsPendingProviderResponse.length === 0 &&
        pendingAssignmentRequests.length === 0 &&
        !hasPendingProfileTasks && (
          <div className="tw:my-6">
            {/* TODO: switch this to provider title and last name? */}
            <AllSetStatus providerDisplayName={provider?.display_name} />
          </div>
        )}

      {/* Availability Section */}
      {providerId && (
        <HomeSection
          headingText="Availability"
          components={[
            <ProviderSchedule
              key="providerSchedule"
              readOnly={false}
              isBooking={false}
              onHomePage={true}
            />,
          ]}
          actionText=""
          actionFunction={undefined}
        />
      )}

      {/* TODO: decide what to do about pending connections, if anything */}
      {/* Pending Connections Section
      {!isConnectionRequestsLoading && !connectionRequestsError && (
        <HomeSection
          headingText="Pending Connections"
          components={[
            <ConnectionsPendingFacilityResponse
              key="connectionsPendingFacilityResponse"
              connectionsPendingFacilityResponse={connectionsPendingFacilityResponse}
              // connectionsPendingFacilityResponse={[]}
            />,
          ]}
          actionText="View All"
          actionFunction={() =>
            navigate('/app/provider/connection-requests?statusCategory=in_process')
          }
        />
      )}
      
      // TODO: should we add upcoming shifts?
      {/* Assignments Section */}
      {/* {!isAssignmentsLoading && !assignmentsError && (
        <HomeSection
          headingText="Assignments"
          components={[
            <AssignmentsPendingFacilityResponse
              key="assignmentsPendingFacilityResponse"
              assignments={pendingAssignmentRequests}
              // assignments={[]}
            />,
          ]}
          actionText="View All"
          actionFunction={() => navigate('/app/provider/booking-requests')}
        />
      )} */}
    </div>
  );
}

export default ProviderHomeWrapper;
